import { useState, useMemo, useEffect } from 'react';
import {
  Receipt,
  Search,
  Loader2,
  MoreHorizontal,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle,
  DollarSign,
  Calendar,
  CreditCard,
  Package,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { subscribeToSupplierInvoices, unsubscribe } from '@/lib/realtime';

interface SupplierInvoice {
  id: string;
  organization_id: string;
  location_id: string;
  supplier_id: string | null;
  purchase_order_id: string | null;
  invoice_number: string | null;
  invoice_date: string;
  due_date: string | null;
  subtotal: number;
  tax_amount: number;
  shipping_cost: number;
  discount_amount: number;
  total_amount: number;
  amount_paid: number;
  status: 'pending' | 'received' | 'partial_payment' | 'paid' | 'cancelled' | 'disputed';
  receipt_type: 'po' | 'direct';
  payment_method: string | null;
  payment_reference: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  supplier?: { id: string; name: string } | null;
  location?: { id: string; name: string } | null;
  purchase_order?: { id: string; order_number: string | null } | null;
}

interface InvoiceItem {
  id: string;
  invoice_id: string;
  product_id: string | null;
  raw_item_id: string | null;
  description: string | null;
  quantity: number;
  unit_cost: number;
  total: number;
  quantity_ordered: number | null;
  quantity_variance: number | null;
  product?: { id: string; name: string; sku: string | null } | null;
  raw_item?: { id: string; name: string } | null;
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: Clock },
  received: { label: 'Received', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Package },
  partial_payment: { label: 'Partial Payment', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: DollarSign },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle },
  disputed: { label: 'Disputed', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: AlertCircle },
};

export default function SupplierInvoices() {
  const { organization } = useAuth();
  const { currentLocation } = useLocations();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  // Real-time subscription for supplier invoice updates
  useEffect(() => {
    if (!currentLocation?.id) return;
    const channel = subscribeToSupplierInvoices(currentLocation.id, () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
    });
    return () => { unsubscribe(channel); };
  }, [currentLocation?.id, queryClient]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<SupplierInvoice | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    method: '',
    reference: '',
  });

  // Fetch invoices
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['supplier-invoices', currentLocation?.id],
    queryFn: async () => {
      let query = supabase
        .from('supplier_invoices')
        .select(`
          *,
          supplier:suppliers(id, name),
          location:locations(id, name),
          purchase_order:purchase_orders(id, order_number)
        `)
        .order('created_at', { ascending: false });
      
      if (currentLocation?.id) {
        query = query.eq('location_id', currentLocation.id);
      } else if (organization?.id) {
        query = query.eq('organization_id', organization.id);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as SupplierInvoice[];
    },
    enabled: !!organization?.id,
  });

  // Fetch invoice items
  const fetchInvoiceItems = async (invoiceId: string) => {
    const { data, error } = await supabase
      .from('supplier_invoice_items')
      .select(`
        *,
        product:products(id, name, sku),
        raw_item:raw_inventory_items(id, name)
      `)
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    return data as InvoiceItem[];
  };

  // Record payment mutation
  const recordPayment = useMutation({
    mutationFn: async ({ invoiceId, amount, method, reference }: { invoiceId: string; amount: number; method: string; reference: string }) => {
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice) throw new Error('Invoice not found');

      const newAmountPaid = invoice.amount_paid + amount;
      const newStatus = newAmountPaid >= invoice.total_amount ? 'paid' : 'partial_payment';

      const { error } = await supabase
        .from('supplier_invoices')
        .update({
          amount_paid: newAmountPaid,
          status: newStatus,
          payment_method: method,
          payment_reference: reference,
          paid_at: newStatus === 'paid' ? new Date().toISOString() : null,
        })
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setPaymentData({ amount: '', method: '', reference: '' });
    },
  });

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      // Status filter
      if (statusFilter !== 'all' && invoice.status !== statusFilter) return false;

      // Type filter
      if (typeFilter !== 'all' && invoice.receipt_type !== typeFilter) return false;

      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          invoice.invoice_number?.toLowerCase().includes(query) ||
          invoice.supplier?.name.toLowerCase().includes(query) ||
          invoice.purchase_order?.order_number?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [invoices, statusFilter, typeFilter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const unpaid = invoices.filter(i => ['received', 'pending', 'partial_payment'].includes(i.status));
    return {
      total: invoices.length,
      unpaid: unpaid.length,
      unpaidAmount: unpaid.reduce((sum, i) => sum + (i.total_amount - i.amount_paid), 0),
      thisMonth: invoices.filter(i => {
        const invoiceDate = new Date(i.invoice_date);
        const now = new Date();
        return invoiceDate.getMonth() === now.getMonth() && invoiceDate.getFullYear() === now.getFullYear();
      }).reduce((sum, i) => sum + i.total_amount, 0),
    };
  }, [invoices]);

  const openViewDialog = async (invoice: SupplierInvoice) => {
    setSelectedInvoice(invoice);
    const items = await fetchInvoiceItems(invoice.id);
    setInvoiceItems(items);
    setViewDialogOpen(true);
  };

  const openPaymentDialog = (invoice: SupplierInvoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: (invoice.total_amount - invoice.amount_paid).toString(),
      method: '',
      reference: '',
    });
    setPaymentDialogOpen(true);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Supplier Invoices</h2>
          <p className="text-sm text-muted-foreground">
            Track invoices and payments to suppliers
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Receipt className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={stats.unpaid > 0 ? "border-amber-200 dark:border-amber-800" : ""}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.unpaid}</p>
                <p className="text-xs text-muted-foreground">Unpaid Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <DollarSign className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.unpaidAmount)}</p>
                <p className="text-xs text-muted-foreground">Amount Due</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.thisMonth)}</p>
                <p className="text-xs text-muted-foreground">This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Invoice #, supplier, PO..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="partial_payment">Partial Payment</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Type</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="po">From PO</SelectItem>
              <SelectItem value="direct">Direct Receipt</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Invoices List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading invoices...</span>
          </div>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No invoices found</h3>
              <p className="text-sm text-muted-foreground">
                Invoices will appear here when goods are received
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Invoice
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Supplier
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Type
                </th>
                <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                  Status
                </th>
                <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                  Total
                </th>
                <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                  Paid
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Date
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredInvoices.map((invoice) => {
                const statusConfig = STATUS_CONFIG[invoice.status];
                const StatusIcon = statusConfig.icon;
                const balance = invoice.total_amount - invoice.amount_paid;
                return (
                  <tr key={invoice.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openViewDialog(invoice)}
                        className="font-medium text-sm hover:underline"
                      >
                        {invoice.invoice_number || 'No Number'}
                      </button>
                      {invoice.purchase_order?.order_number && (
                        <p className="text-xs text-muted-foreground">
                          PO: {invoice.purchase_order.order_number}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {invoice.supplier?.name || <span className="text-muted-foreground">No supplier</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-xs">
                        {invoice.receipt_type === 'po' ? 'From PO' : 'Direct'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={cn('gap-1', statusConfig.color)}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className={balance > 0 ? "text-amber-600" : "text-green-600"}>
                        {formatCurrency(invoice.amount_paid)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(invoice.invoice_date).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(invoice)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {balance > 0 && invoice.status !== 'cancelled' && (
                            <DropdownMenuItem onClick={() => openPaymentDialog(invoice)}>
                              <CreditCard className="h-4 w-4 mr-2" />
                              Record Payment
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* View Invoice Dialog */}
      <ResponsiveModal open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Invoice Details</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {selectedInvoice?.invoice_number || 'Invoice'}
              {selectedInvoice?.supplier?.name && ` from ${selectedInvoice.supplier.name}`}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="max-h-[60vh] overflow-y-auto">
            {selectedInvoice && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Invoice Date</p>
                    <p className="font-medium">{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={cn('mt-1', STATUS_CONFIG[selectedInvoice.status].color)}>
                      {STATUS_CONFIG[selectedInvoice.status].label}
                    </Badge>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-semibold">{formatCurrency(selectedInvoice.total_amount)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Balance Due</p>
                    <p className={cn(
                      "font-semibold",
                      selectedInvoice.total_amount - selectedInvoice.amount_paid > 0 ? "text-amber-600" : "text-green-600"
                    )}>
                      {formatCurrency(selectedInvoice.total_amount - selectedInvoice.amount_paid)}
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h4 className="font-medium mb-3">Items</h4>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Item</th>
                          <th className="text-right px-3 py-2 font-medium">Qty</th>
                          <th className="text-right px-3 py-2 font-medium">Unit Cost</th>
                          <th className="text-right px-3 py-2 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {invoiceItems.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-2">
                              {item.product?.name || item.raw_item?.name || item.description || 'Unknown'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {item.quantity}
                              {item.quantity_variance && item.quantity_variance !== 0 && (
                                <span className={item.quantity_variance > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                                  ({item.quantity_variance > 0 ? '+' : ''}{item.quantity_variance})
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(item.unit_cost)}</td>
                            <td className="px-3 py-2 text-right font-medium tabular-nums">{formatCurrency(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t bg-muted/30">
                        <tr>
                          <td colSpan={3} className="px-3 py-2 text-right font-medium">Total</td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums">
                            {formatCurrency(selectedInvoice.total_amount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Notes */}
                {selectedInvoice.notes && (
                  <div>
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/50">
                      {selectedInvoice.notes}
                    </p>
                  </div>
                )}
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {selectedInvoice && selectedInvoice.total_amount - selectedInvoice.amount_paid > 0 && (
              <Button variant="blaze" onClick={() => {
                setViewDialogOpen(false);
                openPaymentDialog(selectedInvoice);
              }}>
                <CreditCard className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            )}
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Payment Dialog */}
      <ResponsiveModal open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Record Payment</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Record a payment for {selectedInvoice?.invoice_number}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {selectedInvoice && (
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Total Amount</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.total_amount)}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Already Paid</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.amount_paid)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-sm font-medium">Balance Due</span>
                    <span className="font-semibold text-amber-600">
                      {formatCurrency(selectedInvoice.total_amount - selectedInvoice.amount_paid)}
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Payment Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select
                  value={paymentData.method}
                  onValueChange={(value) => setPaymentData({ ...paymentData, method: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                    <SelectItem value="credit">Credit/Store Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reference (optional)</Label>
                <Input
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                  placeholder="Check #, transfer ID, etc."
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={() => selectedInvoice && recordPayment.mutate({
                invoiceId: selectedInvoice.id,
                amount: parseFloat(paymentData.amount) || 0,
                method: paymentData.method,
                reference: paymentData.reference,
              })}
              disabled={recordPayment.isPending || !paymentData.amount || !paymentData.method}
            >
              {recordPayment.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Record Payment
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}

